import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { PortalSelectionEvent } from './../portal-menu.component';
import { FavoritePreferences } from './../../user-preferences/favorite-prefences.model';
import { StudentService } from './../../student/student.service';
import { LoginService } from './../../../shared-modules/login/login.service';
import { PortalMenuPreferences } from './../../user-preferences/portal-menu-preferences.model';
import { Portal } from './../../portal.model';
import { UserPreferencesService } from './../../user-preferences/user-preferences.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { UserService } from './../../user/user.service';
import { Student360Service } from './../../../student360/student360.service';
import { Classroom360Service } from './../../../classroom360/classroom360.service';
import { Component, OnInit, EventEmitter, Output, Input, OnDestroy } from '@angular/core';
import { PortalMenuHelper } from '../portal-menu-helper';
import { forkJoin } from 'rxjs';
import { cloneDeep } from 'lodash';

@AutoUnsubscribe({includeArrays: true})
@Component({
    selector: 'favorite-menu',
    templateUrl: './favorite-menu.component.html',
    styleUrls: ['./favorite-menu.component.css']
})
export class FavoriteMenuComponent implements OnInit, OnDestroy {

    // Interface Properties
    @Input() forNavigation: boolean;
    @Input() forSelection: boolean;
    @Input() defaultPreferences: boolean;
    @Input() autoSave: boolean;
    @Output() portalNavigated = new EventEmitter<void>();

    // Properties
    subscriptions: Subscription[] = [];
    portalPreferences: PortalMenuPreferences;
    favoritesPreferences: FavoritePreferences;
    portals: Portal[] = [];
    displayPortals: Portal[] = [];
    selections: {[portalId: number]: boolean} = {};

    // Constructor
    constructor(
        private classroom360Service: Classroom360Service,
        private student360Service: Student360Service,
        private userService: UserService,
        private userPreferencesService: UserPreferencesService,
        private loginService: LoginService,
        private studentService: StudentService
    ) { }

    // Event Functions
    ngOnInit(): void {
        this.subscriptions.push(
            this.userPreferencesService
                .observableFavoritesPreferencesStream$
                .subscribe(favoritePreferences => {
                    if (
                        this.portals 
                        && this.portalPreferences
                        && favoritePreferences
                    ) {
                        this.updatePreferences(favoritePreferences);
                    }
                }),
            this.classroom360Service
                .observableStaffAssignedPortalsStream$
                .subscribe(portals => {
                    if (
                        this.userService.userType != undefined 
                        && this.userService.userType == 1
                    ) {
                        forkJoin(
                            this.userPreferencesService.getStaffMenuPreferences(this.defaultPreferences),
                            this.userPreferencesService.getFavoritesPreferences(this.defaultPreferences)
                        ).subscribe(results => {
                            this.updatePortals(portals, results[0], results[1]);
                        });
                    }
                }),
            this.student360Service
                .observableParentAssignedPortalsStream$
                .subscribe(portals => {
                    if (
                        this.userService.userType != undefined 
                        && this.userService.userType != 1
                        && this.studentService.selectedStudent
                    ) {
                        forkJoin(
                            this.userPreferencesService.getStudentMenuPreferences(this.studentService.selectedStudent.Id, this.defaultPreferences),
                            this.userPreferencesService.getFavoritesPreferences(this.defaultPreferences)
                        ).subscribe(results => {
                            this.updatePortals(portals, results[0], results[1]);
                        });
                    }
                })
        );
    }

    ngOnDestroy() {}

    // Callable Methods
    save() {
        // POST: Saves the preferences we've been adjusting internally
        const studentId = this.userService.userType == 2 && this.studentService.selectedStudent != null && this.studentService.selectedStudent.Id != null ? this.studentService.selectedStudent.Id : 0;
        const xmlStream: string[] = [];
        xmlStream.push(JSON.stringify(this.favoritesPreferences));
        this.userPreferencesService.saveUserPreferences(
            studentId, 
            '/OneSISFavorites', 
            xmlStream, 
            0, 
            '',
            this.defaultPreferences
        ).subscribe(() => {
            if (!this.defaultPreferences) {
                this.userPreferencesService.favoritesPreferences = this.favoritesPreferences;
            }
        });
    }

    expandAll(expanded: boolean) {
        // POST: expands or collapses all of the items
        (this.displayPortals.reduce((acc, val) => acc.concat(val), []) as Portal[]).forEach(portal => {
            portal.Expanded = expanded ? 1 : 0;
        });
    }

    refreshPreferences() {
        // POST: refreshes the display based on the favorite preferences
        // NOTE: We have to do this because there's a bug where mat-menu closing collapses mat-expansion-panels
        this.updatePreferences(this.favoritesPreferences);
    }
    
    // UI Functions
    portalExpanded(portal: Portal) {
        // PRE: a portal menu is expanded
        // POST: saves the portal preferences
        if (portal.IsGroup && this.forNavigation) {
            let changed = false;
            let portalMenuItem = this.favoritesPreferences.FavoritePortals.find(preferenceItem => 
                preferenceItem.PortalName == portal.PortalName
                && preferenceItem.IsGroup
            );
            if (portalMenuItem) {
                if (portalMenuItem.Expanded != (portal.Expanded == 1)) {
                    portalMenuItem.Expanded = portal.Expanded == 1;
                    changed = true;
                }
            } else {
                portalMenuItem = {
                    ParentPortalName: '',
                    PortalDescription: portal.PortalDescription ? portal.PortalDescription.split('?')[0] : '',
                    PortalName: portal.PortalName,
                    PortalModule: portal.ParentModule,
                    GroupName: portal.GroupName,
                    Expanded: portal.Expanded == 1,
                    Id: portal.Id,
                    IsGroup: true
                }
                this.favoritesPreferences.FavoritePortals.push(portalMenuItem);
                changed = true;
            }
            if (this.autoSave && changed) {
                this.save();
            }
        }
    }

    portalSelected(event: PortalSelectionEvent) {
        // PRE: a portal is selected
        // POST: saves the portal as a new favorite preference
        if (!event.portal.IsGroup && this.forSelection) {
            let portalMenuItemIndex = this.favoritesPreferences.FavoritePortals.findIndex(preferenceItem => 
                preferenceItem.PortalName == event.portal.PortalName
                && !preferenceItem.IsGroup
            );
            let changed = false;
            if (portalMenuItemIndex >= 0 && !event.selected) {
                this.favoritesPreferences.FavoritePortals.splice(portalMenuItemIndex, 1);
                changed = true;
            } else if (portalMenuItemIndex < 0 && event.selected) {
                this.favoritesPreferences.FavoritePortals.push({
                    ParentPortalName: '',
                    PortalDescription: event.portal.PortalDescription ? event.portal.PortalDescription.split('?')[0] : '',
                    PortalName: event.portal.PortalName,
                    PortalModule: event.portal.ParentModule,
                    GroupName: event.portal.GroupName,
                    Expanded: event.portal.Expanded == 1,
                    Id: event.portal.Id,
                    IsGroup: false
                });
                changed = true;
            }
            if (this.autoSave && changed) {
                this.save();
            }
        }
    }

    // Internal Methods
    private updatePortals(
        portals: Portal[],
        portalPreferences?: PortalMenuPreferences, 
        favoritesPreferences?: FavoritePreferences
    ) {
        // POST: updates portals and preferences
        if (portalPreferences) {
            this.portalPreferences = portalPreferences;
        }
        if (this.portalPreferences) {
            this.portals = PortalMenuHelper.organizePortals(
                portals,
                this.portalPreferences,
                this.loginService.LoginSettings.V10 && this.userService.staffUserStudentLocked
            );
        }
        this.updatePreferences(favoritesPreferences);
    }

    private updatePreferences(favoritesPreferences: FavoritePreferences) {
        // PRE: the portals have been updated
        // POST: recursively adjusts the portals according to preferences
        if (favoritesPreferences) {
            this.favoritesPreferences = cloneDeep(favoritesPreferences);
        }
        if (this.favoritesPreferences && this.portals) {
            if (this.forNavigation) {
                const tmpDisplayPortals = this.expandFavoritesRecursive(
                    this.filterFavoritesRecursive(
                        cloneDeep(this.portals)
                    )
                );
                this.displayPortals = cloneDeep(tmpDisplayPortals.filter(x => x.ChildPortals.length > 0));
            } else {
                this.displayPortals = cloneDeep(this.portals);
                this.expandAll(true);
            }
            this.selections = {};
            this.favoritesPreferences.FavoritePortals.forEach(favoritePortal => {
                this.selections[favoritePortal.Id] = true;
            });
        }
    }

    private filterFavoritesRecursive(portals: Portal[]): Portal[] {
        // PRE: the portals are defined
        // POST: filters the portals down to the favorites
        return portals.filter(portal => {
            if (portal.ChildPortals) {
                portal.ChildPortals = this.filterFavoritesRecursive(portal.ChildPortals);
            }
            const matchingFavorite = this.favoritesPreferences.FavoritePortals.find(favorite =>
                favorite.PortalName == portal.PortalName
                && favorite.GroupName == portal.GroupName
                && favorite.PortalModule == portal.ParentModule
            );
            return portal.ChildPortals.length > 0 || matchingFavorite;
        });
    }

    private expandFavoritesRecursive(portals: Portal[]): Portal[] {
        // PRE: the portals and the favorite preferences are defined
        // POST: expands the portals recursively
        portals.forEach(portal => {
            const matchingFavorite = this.favoritesPreferences.FavoritePortals.find(favorite =>
                favorite.PortalName == portal.PortalName
                && favorite.IsGroup
            );
            if (matchingFavorite) {
                portal.Expanded = matchingFavorite.Expanded ? 1 : 0;
            }
            if (portal.ChildPortals.length > 0) {
                this.expandFavoritesRecursive(portal.ChildPortals);
            }
        });
        return portals;
    }
}
